import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Package, Loader2, Plus, Save, Trash2, RefreshCw, X, Pencil, AlertTriangle,
  Sun, Zap, Battery, Plug, Wrench, RotateCcw, FileUp, Sparkles, Sliders, Settings2, Palette, Copy
} from "lucide-react";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";

const inputCls = "h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium";
const labelCls = "block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold";
const eur = (v) => {
  try { return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(v) || 0); }
  catch { return `${v} €`; }
};

const TYPE_META = {
  module:   { icon: Sun,     label: "Modul" },
  inverter: { icon: Zap,     label: "Wechselrichter" },
  battery:  { icon: Battery, label: "Speicher" },
  wallbox:  { icon: Plug,    label: "Wallbox" },
  addon:    { icon: Wrench,  label: "Zusatzleistung" },
  frei:     { icon: Wrench,  label: "Freie Position" },
};

const emptyDraft = () => ({
  name: "",
  description: "",
  target_kwp: 0,
  items: [],
  notes: "",
  active: true,
});

// Netto-Einzelpreis pro Item. `ref_price_net_eur` ist per Definition der
// Einzelpreis, `line_price_net_eur` die Zeilensumme (ref × qty). Bei
// Auto-generierten Paketen mit qty>1 (z.B. 32×475Wp) ergab MAX(ref, line)
// einen qty²-Fehler — daher jetzt priorisiert `ref`, dann Fallback auf
// `line/qty`, zuletzt Komponentenpreis.
const _itemUnit = (it, compMap) => {
  const refP = Number(it.ref_price_net_eur) || 0;
  if (refP > 0) return refP;
  const lineP = Number(it.line_price_net_eur) || 0;
  const qty = Number(it.qty) || 0;
  if (lineP > 0 && qty > 0) return Math.round((lineP / qty) * 100) / 100;
  const c = it.component_id ? compMap[it.component_id] : null;
  return c ? (Number(c.price_eur) || 0) : 0;
};

export default function AdminPvPackages({ embedded = false }) {
  const [pkgs, setPkgs] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [pickerType, setPickerType] = useState("module");
  const [seeding, setSeeding] = useState(false);
  // PDF/Bild-Import — 2-Schritt: extract (LLM) → Preview (importPreview) → commit
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [committing, setCommitting] = useState(false);

  // Iter56 — Live-Marge-Regler: zeigt in der Liste den margin-adjustierten
  // Endkundenpreis, ohne dass der User im Kopf rechnen muss. Nicht persistiert;
  // Marge kommt aus User-/Tenant-Config. Slider ist nur ein Visualisierer.
  const [previewMarginPct, setPreviewMarginPct] = useState(0);

  // Iter56 — Tenant-Referenzpreise €/kWp (Budget/Premium) für die
  // Google-Solar-Auto-Paket-Vorschläge. Wird pro Mandant persistiert.
  const [kwpPrices, setKwpPrices] = useState({ budget: 1950, premium: 2400 });
  const [kwpDefaults, setKwpDefaults] = useState({ budget: 1950, premium: 2400 });
  const [kwpDirty, setKwpDirty] = useState(false);
  const [kwpSaving, setKwpSaving] = useState(false);

  // Iter57 — Tenant-Brand-Color (Hex) für PV-Amortisations-Chart
  const [brandColor, setBrandColor] = useState("");
  const [brandColorDirty, setBrandColorDirty] = useState(false);
  const [brandColorSaving, setBrandColorSaving] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [{ data: pks }, { data: cat }] = await Promise.all([
        api.get(`/admin/pv-packages?show_inactive=${showInactive}`),
        api.get("/pv/catalog"),
      ]);
      setPkgs(pks || []);
      setCatalog(cat || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Daten konnten nicht geladen werden");
    } finally { setLoading(false); }
  };

  // Tenant kWp-Referenzpreise laden (nicht blockierend — Liste lädt parallel)
  const loadKwpPrices = async () => {
    try {
      const { data } = await api.get("/admin/pv-kwp-prices");
      setKwpPrices({ budget: Number(data.budget) || 1950, premium: Number(data.premium) || 2400 });
      setKwpDefaults(data.defaults || { budget: 1950, premium: 2400 });
      setKwpDirty(false);
    } catch (e) {
      // Wenn Endpoint (noch) nicht verfügbar ist, zeigen wir nur Fallback
      // und blockieren die Seite nicht.
    }
  };
  useEffect(() => { loadKwpPrices(); }, []);

  // Iter57 — Brand Color Loader/Saver
  const loadBrandColor = async () => {
    try {
      const { data } = await api.get("/admin/tenant/brand-color");
      setBrandColor(data.color || "");
      setBrandColorDirty(false);
    } catch (e) {
      // silent — feature nicht kritisch
    }
  };
  useEffect(() => { loadBrandColor(); }, []);

  const saveBrandColor = async () => {
    const val = (brandColor || "").trim();
    // Erlaubt: leerer String (reset) oder gültiger Hex
    if (val && !/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(val)) {
      toast.error("Bitte gültige Hex-Farbe verwenden (z. B. #0EA5E9)");
      return;
    }
    setBrandColorSaving(true);
    try {
      await api.put("/admin/tenant/brand-color", { color: val || null });
      toast.success(val ? "Brand-Farbe gespeichert" : "Brand-Farbe zurückgesetzt (Fallback grün)");
      setBrandColorDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBrandColorSaving(false); }
  };

  const saveKwpPrices = async () => {
    if (!(kwpPrices.budget > 0) || !(kwpPrices.premium > 0)) {
      toast.error("Beide Preise müssen > 0 sein");
      return;
    }
    setKwpSaving(true);
    try {
      await api.put("/admin/pv-kwp-prices", {
        budget: Number(kwpPrices.budget),
        premium: Number(kwpPrices.premium),
      });
      toast.success("kWp-Referenzpreise gespeichert");
      setKwpDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setKwpSaving(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [showInactive]);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const compMap = useMemo(() => {
    const m = {};
    for (const c of catalog) m[c.id] = c;
    return m;
  }, [catalog]);

  const filteredCatalog = useMemo(
    () => catalog.filter(c => c.type === pickerType && c.active !== false),
    [catalog, pickerType]
  );

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/pv-packages/seed-demo");
      const msg = `${data.inserted || 0} angelegt · ${data.skipped || 0} übersprungen`;
      toast.success(`Demo-Seed fertig: ${msg}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Seed fehlgeschlagen");
    } finally { setSeeding(false); }
  };

  // -----------------------------------------------------------------
  // PDF/Bild-Import — Schritt 1: LLM extrahiert Positionen + matched Komponenten
  const onImportPick = () => fileInputRef.current?.click();
  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 15 MB).");
      return;
    }
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/pv-packages/import-extract", form, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
      });
      // items für Editor-Vorschau normalisieren
      setImportPreview({
        name: data.name || "PV-Paket (Import)",
        description: data.description || "",
        target_kwp: Number(data.target_kwp) || 0,
        source_tag: data.source_tag || "pdf_import",
        items: (data.items || []).map((it, idx) => ({
          _key: `imp-${idx}`,
          role: it.role || "addon",
          brand: it.brand || "",
          model: it.model || "",
          qty: Number(it.qty) || 1,
          price_net_eur: Number(it.price_net_eur) || 0,
          kwp_or_kw: Number(it.kwp_or_kw) || 0,
          description: it.description || "",
          matched_component_id: it.matched_component_id || null,
          match_confidence: Number(it.match_confidence) || 0,
        })),
      });
      toast.success(`${(data.items || []).length} Positionen erkannt — bitte prüfen.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import fehlgeschlagen");
    } finally { setImporting(false); }
  };

  // Preview-Modal: einzelne Zeile ändern
  const updateImportItem = (key, patch) => {
    setImportPreview((p) => ({
      ...p,
      items: p.items.map(i => i._key === key ? { ...i, ...patch } : i),
    }));
  };
  const removeImportItem = (key) => {
    setImportPreview((p) => ({ ...p, items: p.items.filter(i => i._key !== key) }));
  };

  const importGrossPreview = useMemo(() => {
    if (!importPreview) return 0;
    return importPreview.items.reduce(
      (s, it) => s + (Number(it.price_net_eur) || 0) * (Number(it.qty) || 0), 0
    );
  }, [importPreview]);

  // Schritt 2: bestätigte Struktur an Backend → Paket wird angelegt → Liste neu laden
  const commitImport = async () => {
    if (!importPreview) return;
    if (!importPreview.name.trim()) {
      toast.error("Bitte einen Paket-Namen vergeben");
      return;
    }
    if (importPreview.items.length === 0) {
      toast.error("Keine Positionen — Import abbrechen oder Zeilen hinzufügen");
      return;
    }
    setCommitting(true);
    try {
      const payload = {
        name: importPreview.name.trim(),
        description: importPreview.description || "",
        target_kwp: Number(importPreview.target_kwp) || 0,
        source_tag: importPreview.source_tag,
        items: importPreview.items.map(i => ({
          role: i.role,
          brand: i.brand || null,
          model: i.model || null,
          qty: Number(i.qty) || 0,
          price_net_eur: Number(i.price_net_eur) || 0,
          kwp_or_kw: Number(i.kwp_or_kw) || null,
          description: i.description || null,
          matched_component_id: i.matched_component_id || null,
          match_confidence: Number(i.match_confidence) || 0,
        })),
      };
      const { data } = await api.post("/admin/pv-packages/import-commit", payload);
      toast.success(`Paket angelegt: ${data.name}`);
      setImportPreview(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setCommitting(false); }
  };
  // -----------------------------------------------------------------

  const openNew = () => setEditor({ isNew: true, ...emptyDraft() });
  const openEdit = (pkg) => {
    const rawItems = (pkg.items || []).map((it, idx) => {
      const compPrice = it.component_id ? Number((compMap[it.component_id] || {}).price_eur) || 0 : 0;
      // Fix Iter88zj — Einzelpreis IMMER aus ref_price_net_eur (nie MAX(ref, line)),
      // sonst 32×-Fehler bei Auto-Paketen. Fallback: line/qty, dann Komponentenpreis.
      const refP = Number(it.ref_price_net_eur) || 0;
      const lineP = Number(it.line_price_net_eur) || 0;
      const qty = Number(it.qty) || 1;
      let unified = 0;
      if (refP > 0) unified = refP;
      else if (lineP > 0 && qty > 0) unified = Math.round((lineP / qty) * 100) / 100;
      else unified = compPrice;
      return {
        _key: it.component_id ? `cid:${it.component_id}:${idx}` : `free:${idx}:${Math.random().toString(36).slice(2, 8)}`,
        component_id: it.component_id || null,
        is_free: !!it.is_free || !it.component_id,
        title: it.title || "",
        qty,
        note: it.note || "",
        ref_price_net_eur: unified,
        line_price_net_eur: unified,
      };
    });
    setEditor({
      isNew: false, id: pkg.id,
      name: pkg.name || "", description: pkg.description || "",
      target_kwp: pkg.target_kwp || 0,
      items: rawItems,
      notes: pkg.notes || "",
      active: pkg.active !== false,
    });
  };

  const updateItem = (key, patch) => {
    setEditor(ed => ({ ...ed, items: ed.items.map(i => i._key === key ? { ...i, ...patch } : i) }));
  };
  const updateQty = (key, qty) => updateItem(key, { qty: Math.max(0, Number(qty) || 0) });
  // Ein Preis pro Item — ref und line werden IMMER synchron gehalten.
  const updateRefPrice = (key, val) => {
    const p = Math.max(0, Number(val) || 0);
    updateItem(key, { ref_price_net_eur: p, line_price_net_eur: p });
  };
  const updateNote = (key, note) => updateItem(key, { note });
  const updateFreeTitle = (key, title) => updateItem(key, { title });
  const removeItem = (key) => setEditor(ed => ({ ...ed, items: ed.items.filter(i => i._key !== key) }));

  const addItem = (componentId) => {
    if (!editor) return;
    const c = compMap[componentId] || {};
    const price = Math.max(0, Number(c.price_eur) || 0);
    setEditor({
      ...editor,
      items: [...editor.items, {
        _key: `cid:${componentId}:${Date.now().toString(36)}`,
        component_id: componentId,
        is_free: false,
        title: "",
        qty: 1, note: "",
        ref_price_net_eur: price,
        line_price_net_eur: price,
      }],
    });
  };

  const addFreeLine = () => {
    if (!editor) return;
    setEditor({
      ...editor,
      items: [...editor.items, {
        _key: `free:${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
        component_id: null, is_free: true, title: "", qty: 1, note: "",
        ref_price_net_eur: 0, line_price_net_eur: 0,
      }],
    });
  };

  // Übernimmt für alle Katalog-Zeilen den aktuellen Netto-Preis aus PV-Komponenten
  const pullPricesFromComponents = () => {
    if (!editor) return;
    let updated = 0;
    const next = editor.items.map(it => {
      if (it.is_free || !it.component_id) return it;
      const c = compMap[it.component_id];
      if (!c) return it;
      const p = Math.max(0, Number(c.price_eur) || 0);
      if (p <= 0) return it;
      if (it.ref_price_net_eur !== p || it.line_price_net_eur !== p) {
        updated += 1;
        return { ...it, ref_price_net_eur: p, line_price_net_eur: p };
      }
      return it;
    });
    setEditor({ ...editor, items: next });
    if (updated === 0) toast.info("Preise sind bereits synchron mit PV-Komponenten.");
    else toast.success(`${updated} Position(en) aus PV-Komponenten aktualisiert.`);
  };

  // Live-Summe
  const linesSumNet = useMemo(() => {
    if (!editor) return 0;
    return (editor.items || []).reduce(
      (s, it) => s + _itemUnit(it, compMap) * (Number(it.qty) || 0), 0
    );
  }, [editor, compMap]);
  // PV = 0 % USt § 12 Abs. 3 UStG → Netto = Brutto in Editor & Listen.
  const linesSumGross = linesSumNet;
  const kwpFromModules = useMemo(() => {
    if (!editor) return 0;
    return (editor.items || [])
      .filter(it => !it.is_free && compMap[it.component_id]?.type === "module")
      .reduce((s, it) => s + ((compMap[it.component_id]?.kwp_or_kw || 0) * (Number(it.qty) || 0)), 0);
  }, [editor, compMap]);

  const save = async () => {
    if (!editor) return;
    if (!editor.name) { toast.error("Bitte einen Paket-Namen vergeben"); return; }
    if (editor.items.length === 0) { toast.error("Paket braucht mindestens eine Komponente"); return; }
    setSaving(true);
    try {
      const payload = {
        name: editor.name,
        description: editor.description || "",
        target_kwp: Number(editor.target_kwp) || 0,
        items: editor.items.map(i => ({
          component_id: i.component_id || null,
          qty: Number(i.qty) || 0,
          ref_price_net_eur: Number(i.ref_price_net_eur) || 0,
          line_price_net_eur: Number(i.line_price_net_eur) || 0,
          is_free: !!i.is_free,
          title: i.title || null,
          note: i.note || "",
        })),
        notes: editor.notes || "",
        active: editor.active,
      };
      if (editor.isNew) {
        await api.post("/admin/pv-packages", payload);
        toast.success("Paket angelegt");
      } else {
        await api.patch(`/admin/pv-packages/${editor.id}`, payload);
        toast.success("Paket aktualisiert");
      }
      setEditor(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const remove = async (pkg) => {
    if (!window.confirm(`Paket "${pkg.name}" deaktivieren? Bestehende Konfigurationen bleiben unverändert.`)) return;
    try {
      await api.delete(`/admin/pv-packages/${pkg.id}`);
      toast.success("Deaktiviert");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehlgeschlagen");
    }
  };

  const duplicatePackage = async (pkg) => {
    // Iter88c: „Als eigene Kopie" — bei geerbten Paketen wird das Duplikat
    // dem aktuell wirkenden Tenant zugewiesen (Backend cloned mit
    // tenant_id = current_acting_tenant).
    const isInherited = !!pkg.is_inherited;
    const defaultName = isInherited
      ? `${pkg.name || "Paket"} (Kopie)`
      : `${pkg.name || "Paket"} (Kopie)`;
    const chosen = window.prompt(
      isInherited
        ? "Als eigene Kopie duplizieren. Name der neuen Kopie:"
        : "Name der Kopie:",
      defaultName,
    );
    if (chosen === null) return;
    const finalName = chosen.trim() || defaultName;
    try {
      const { data: copy } = await api.post(`/admin/pv-packages/${pkg.id}/duplicate`);
      if (finalName !== copy.name) {
        try {
          await api.patch(`/admin/pv-packages/${copy.id}`, {
            ...copy,
            name: finalName,
            items: copy.items || [],
          });
        } catch {
          /* Fallback: Kopie bleibt mit Default-Namen */
        }
      }
      toast.success(`Kopiert: „${finalName}"`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kopieren fehlgeschlagen");
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-pv-packages">
      {!embedded && <AppHeader title="PV-Pakete" back="/dashboard" />}
      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-3">
            <Package className="text-[#00E575]" size={22} />
            <div>
              <h1 className="font-display text-xl sm:text-2xl font-bold">PV-Pakete</h1>
              <p className="text-[12px] text-zinc-300 font-medium">Vorgeschnürte Komplettsets aus PV-Komponenten · Preis wird live aus den Positionen berechnet</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-[12px] text-zinc-400 inline-flex items-center gap-2 cursor-pointer font-medium">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}
                data-testid="pv-pkgs-toggle-inactive" className="accent-[#00E575]" />
              Inaktive
            </label>
            <button onClick={load} data-testid="pv-pkgs-reload"
              className="h-9 px-3 rounded-lg bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/40 text-[12px] inline-flex items-center gap-1.5 font-medium">
              <RefreshCw size={13} /> Aktualisieren
            </button>
            <button onClick={seedDemo} disabled={seeding} data-testid="pv-pkgs-seed-demo"
              title="Standard-PV-Pakete (8 / 12 / 16 kWp) anlegen — idempotent"
              className="h-9 px-3 rounded-lg bg-[#18181B] border border-amber-500/40 hover:border-amber-400 text-amber-300 font-semibold text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50">
              {seeding ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
              Demo-Seed
            </button>
            <button onClick={onImportPick} disabled={importing} data-testid="pv-pkgs-import"
              title="Angebot (PDF, Bild, Excel oder CSV) hochladen — die KI extrahiert Positionen und ordnet sie den PV-Komponenten zu"
              className="h-9 px-3 rounded-lg bg-[#18181B] border border-[#00E575]/40 hover:border-[#00E575] text-[#00E575] font-semibold text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50">
              {importing ? <Loader2 size={13} className="animate-spin" /> : <FileUp size={13} />}
              Aus Datei (PDF/Excel)
            </button>
            <input ref={fileInputRef} type="file" accept="application/pdf,image/*,.xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              className="hidden" onChange={onImportFile} data-testid="pv-pkgs-import-file" />
            <button onClick={openNew} data-testid="pv-pkgs-new"
              className="h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964]">
              <Plus size={14} /> Neues Paket
            </button>
          </div>
        </div>

        {/* Iter56/57 — Marge-Regler + Tenant-Referenzpreise + Chart-Brand-Color */}
        <section
          className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 grid grid-cols-1 md:grid-cols-3 gap-4"
          data-testid="pv-admin-tools"
        >
          {/* Margin Slider */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sliders size={14} className="text-[#00E575]" />
              <h3 className="text-[13px] font-bold uppercase tracking-wider text-zinc-100">
                Marge-Regler (Vorschau)
              </h3>
            </div>
            <p className="text-[11.5px] text-zinc-400 mb-3 font-medium">
              Simuliert live den Endkundenpreis. True-Margin: <code className="text-zinc-300">Preis / (1 − Marge)</code>.
              Nicht persistiert – nur Anzeige-Visualisierung.
            </p>
            <div className="flex items-center gap-3">
              <input
                type="range" min="0" max="50" step="1"
                value={previewMarginPct}
                onChange={(e) => setPreviewMarginPct(Number(e.target.value))}
                data-testid="pv-pkg-margin-slider"
                className="flex-1 accent-[#00E575]"
              />
              <span
                className="font-mono text-[15px] font-bold text-[#00E575] w-16 text-right"
                data-testid="pv-pkg-margin-value"
              >
                {previewMarginPct} %
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {[0, 10, 15, 20, 25, 30].map((v) => (
                <button
                  key={v}
                  onClick={() => setPreviewMarginPct(v)}
                  data-testid={`pv-pkg-margin-chip-${v}`}
                  className={`text-[11px] font-semibold px-2 py-0.5 rounded border transition ${
                    previewMarginPct === v
                      ? "bg-[#00E575]/20 border-[#00E575] text-[#00E575]"
                      : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-[#00E575]/50"
                  }`}
                >
                  {v} %
                </button>
              ))}
            </div>
          </div>

          {/* Tenant kWp-Referenzpreise */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Settings2 size={14} className="text-sky-400" />
              <h3 className="text-[13px] font-bold uppercase tracking-wider text-zinc-100">
                Google-Solar kWp-Referenzpreise
              </h3>
            </div>
            <p className="text-[11.5px] text-zinc-400 mb-3 font-medium">
              Netto € pro kWp (0 % USt). Wird für die Auto-Paket-Vorschläge (Budget/Premium)
              in der Google-Solar-Dachanalyse verwendet.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10.5px] uppercase tracking-wider text-zinc-400 font-bold mb-1">
                  Budget (Fox ESS)
                </label>
                <div className="relative">
                  <input
                    type="number" min="100" max="10000" step="10"
                    value={kwpPrices.budget}
                    onChange={(e) => { setKwpPrices((p) => ({ ...p, budget: Number(e.target.value) || 0 })); setKwpDirty(true); }}
                    data-testid="pv-kwp-budget"
                    className="h-9 w-full bg-[#09090B] border border-zinc-700 rounded pl-2 pr-10 text-[13px] font-mono text-zinc-100 focus:border-[#00E575] outline-none"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-zinc-500">€/kWp</span>
                </div>
                <span className="text-[10px] text-zinc-500 mt-0.5 block">Fallback: {kwpDefaults.budget} €</span>
              </div>
              <div>
                <label className="block text-[10.5px] uppercase tracking-wider text-zinc-400 font-bold mb-1">
                  Premium (Sigenergy)
                </label>
                <div className="relative">
                  <input
                    type="number" min="100" max="10000" step="10"
                    value={kwpPrices.premium}
                    onChange={(e) => { setKwpPrices((p) => ({ ...p, premium: Number(e.target.value) || 0 })); setKwpDirty(true); }}
                    data-testid="pv-kwp-premium"
                    className="h-9 w-full bg-[#09090B] border border-zinc-700 rounded pl-2 pr-10 text-[13px] font-mono text-zinc-100 focus:border-sky-400 outline-none"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-zinc-500">€/kWp</span>
                </div>
                <span className="text-[10px] text-zinc-500 mt-0.5 block">Fallback: {kwpDefaults.premium} €</span>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-2">
              {kwpDirty && (
                <span className="text-[10.5px] text-amber-400 font-semibold">Änderungen nicht gespeichert</span>
              )}
              <button
                onClick={saveKwpPrices}
                disabled={!kwpDirty || kwpSaving}
                data-testid="pv-kwp-save"
                className="h-8 px-3 rounded bg-sky-500/15 hover:bg-sky-500/25 border border-sky-500/40 text-sky-200 font-semibold text-[11.5px] inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {kwpSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Speichern
              </button>
            </div>
          </div>

          {/* Iter57 — PV-Chart Brand-Color (Amortisations-Chart) */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Palette size={14} className="text-fuchsia-400" />
              <h3 className="text-[13px] font-bold uppercase tracking-wider text-zinc-100">
                Chart-Brandfarbe
              </h3>
            </div>
            <p className="text-[11.5px] text-zinc-400 mb-3 font-medium">
              Optional. Färbt die Ersparnis-Linie & Break-Even-Marker im PV-Angebot-PDF.
              Leer lassen für Fallback (Grün). Rote Investitions-Linie bleibt fest.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={brandColor && /^#[0-9a-fA-F]{6}$/.test(brandColor) ? brandColor : "#16A34A"}
                onChange={(e) => { setBrandColor(e.target.value); setBrandColorDirty(true); }}
                data-testid="pv-brand-color-picker"
                className="h-9 w-12 rounded border border-zinc-700 bg-[#09090B] cursor-pointer"
                aria-label="Brand Farbe"
              />
              <input
                type="text"
                value={brandColor}
                onChange={(e) => { setBrandColor(e.target.value); setBrandColorDirty(true); }}
                placeholder="#0EA5E9 (leer = grün)"
                data-testid="pv-brand-color-input"
                className="h-9 flex-1 bg-[#09090B] border border-zinc-700 rounded px-2 text-[13px] font-mono text-zinc-100 focus:border-fuchsia-400 outline-none"
              />
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {["#16A34A", "#0EA5E9", "#EAB308", "#EC4899", "#F97316", ""].map((c, i) => (
                <button
                  key={i}
                  onClick={() => { setBrandColor(c); setBrandColorDirty(true); }}
                  data-testid={`pv-brand-color-preset-${c ? c.replace("#", "") : "reset"}`}
                  className={`h-6 w-10 rounded border transition ${
                    brandColor === c
                      ? "border-fuchsia-400 ring-1 ring-fuchsia-400"
                      : "border-zinc-700 hover:border-zinc-500"
                  }`}
                  style={{ background: c || "transparent" }}
                  title={c || "Zurücksetzen (Fallback grün)"}
                >
                  {!c && <span className="text-[10px] text-zinc-400">reset</span>}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-end gap-2 mt-2">
              {brandColorDirty && (
                <span className="text-[10.5px] text-amber-400 font-semibold">Nicht gespeichert</span>
              )}
              <button
                onClick={saveBrandColor}
                disabled={!brandColorDirty || brandColorSaving}
                data-testid="pv-brand-color-save"
                className="h-8 px-3 rounded bg-fuchsia-500/15 hover:bg-fuchsia-500/25 border border-fuchsia-500/40 text-fuchsia-200 font-semibold text-[11.5px] inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {brandColorSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Speichern
              </button>
            </div>
          </div>
        </section>

        {loading && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 flex items-center justify-center gap-2 text-zinc-200 font-semibold">
            <Loader2 size={16} className="animate-spin" /> lädt…
          </div>
        )}

        {!loading && pkgs.length === 0 && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-300 text-sm font-medium">
            Noch keine PV-Pakete angelegt. Klick auf <b className="text-[#00E575]">Neues Paket</b> um ein Komplettset zu schnüren.
          </div>
        )}

        {!loading && pkgs.map((p) => {
          const gross = Number(p.price_eur_gross || p.price_eur || 0);
          // Iter56 — Live-Marge-Vorschau (True-Margin): Preis / (1 − Marge)
          const marginFactor = 1 - Math.min(previewMarginPct, 49.9) / 100;
          const grossWithMargin = marginFactor > 0 ? gross / marginFactor : gross;
          const marginDelta = grossWithMargin - gross;
          return (
          <section key={p.id} className={`bg-[#18181B] border rounded-2xl p-4 ${p.active ? "border-zinc-800" : "border-zinc-800 opacity-60"}`} data-testid={`pv-pkg-row-${p.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h2 className="font-display text-lg font-bold">{p.name}</h2>
                {p.description && <p className="text-[12px] text-zinc-400 mt-0.5 font-medium">{p.description}</p>}
                <div className="flex flex-wrap gap-2 mt-2">
                  {p.target_kwp > 0 && (
                    <span className="text-[10.5px] font-bold uppercase tracking-wider bg-[#00E575]/10 text-[#00E575] px-2 py-0.5 rounded">{p.target_kwp} kWp</span>
                  )}
                  <span className="text-[10.5px] font-bold uppercase tracking-wider bg-zinc-800 text-zinc-300 px-2 py-0.5 rounded">{(p.items || []).length} Posten</span>
                  {!p.active && <span className="text-[10.5px] font-bold uppercase tracking-wider bg-amber-500/10 text-amber-400 px-2 py-0.5 rounded">inaktiv</span>}
                  {p.is_inherited && (
                    <span
                      className="text-[10.5px] font-bold uppercase tracking-wider bg-sky-500/15 border border-sky-500/40 text-sky-300 px-2 py-0.5 rounded"
                      data-testid={`pv-pkg-inherited-${p.id}`}
                      title='Geerbt vom Eltern-Mandanten — zum Individualisieren "Als eigene Kopie" duplizieren.'
                    >
                      Geerbt
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold">Netto-Preis</div>
                <div className="font-mono text-xl font-bold text-[#00E575]" data-testid={`pv-pkg-gross-${p.id}`}>{eur(gross)}</div>
                <div className="text-[10.5px] text-zinc-400 mt-0.5">Σ Komponenten (0 % USt)</div>
                {previewMarginPct > 0 && (
                  <div className="mt-2 pt-1.5 border-t border-zinc-800" data-testid={`pv-pkg-margin-preview-${p.id}`}>
                    <div className="text-[10.5px] uppercase tracking-widest text-sky-300 font-semibold">
                      + {previewMarginPct} % Marge
                    </div>
                    <div className="font-mono text-[15px] font-bold text-sky-200">{eur(grossWithMargin)}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">Marge = {eur(marginDelta)}</div>
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-x-auto -mx-1 mb-3">
              <table className="w-full text-[12px] font-medium">
                <thead>
                  <tr className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold border-b border-zinc-800">
                    <th className="text-left py-1.5 px-2">Typ</th>
                    <th className="text-left py-1.5 px-2">Komponente</th>
                    <th className="text-center py-1.5 px-2">Menge</th>
                    <th className="text-right py-1.5 px-2">Einzelpreis</th>
                    <th className="text-right py-1.5 px-2">Zwischensumme</th>
                  </tr>
                </thead>
                <tbody>
                  {(p.items || []).map((it, idx) => {
                    const Meta = TYPE_META[it.type] || TYPE_META.module;
                    const Icon = Meta.icon;
                    return (
                      <tr key={idx} className={`border-b border-zinc-800/40 ${it.missing ? "text-red-400" : ""}`}>
                        <td className="py-1.5 px-2"><span className="inline-flex items-center gap-1.5 text-zinc-400"><Icon size={11} /> {Meta.label}</span></td>
                        <td className="py-1.5 px-2 text-zinc-100">{it.missing ? "⚠ gelöschte Komponente" : (it.is_free ? (it.title || "Freie Position") : `${it.brand || ""} ${it.model || ""}`.trim())}</td>
                        <td className="py-1.5 px-2 text-center text-zinc-300">{it.qty}×</td>
                        <td className="py-1.5 px-2 text-right text-zinc-400 font-mono">{eur(it.unit_price)}</td>
                        <td className="py-1.5 px-2 text-right text-zinc-100 font-mono">{eur(it.line_total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2">
              {p.is_inherited ? (
                <button onClick={() => duplicatePackage(p)} data-testid={`pv-pkg-duplicate-${p.id}`}
                  className="h-8 px-3 rounded-lg bg-sky-500/15 border border-sky-500/50 text-sky-200 hover:bg-sky-500/25 inline-flex items-center gap-1.5 text-[11.5px] font-semibold">
                  <Copy size={12} /> Als eigene Kopie
                </button>
              ) : (
                <>
                  <button onClick={() => openEdit(p)} data-testid={`pv-pkg-edit-${p.id}`}
                    className="h-8 px-3 rounded-lg bg-[#09090B] border border-zinc-700 hover:border-[#00E575]/50 inline-flex items-center gap-1.5 text-[11.5px]">
                    <Pencil size={12} /> Bearbeiten
                  </button>
                  <button onClick={() => duplicatePackage(p)} data-testid={`pv-pkg-duplicate-${p.id}`}
                    className="h-8 px-3 rounded-lg bg-[#09090B] border border-sky-700 hover:border-sky-500/70 text-sky-300 inline-flex items-center gap-1.5 text-[11.5px]">
                    <Copy size={12} /> Duplizieren
                  </button>
                  {p.active ? (
                    <button onClick={() => remove(p)} data-testid={`pv-pkg-del-${p.id}`}
                      className="h-8 px-3 rounded-lg bg-[#09090B] border border-zinc-700 hover:border-red-500/50 hover:text-red-400 inline-flex items-center gap-1.5 text-[11.5px]">
                      <Trash2 size={12} /> Deaktivieren
                    </button>
                  ) : (
                <button
                  onClick={async () => {
                    try {
                      await api.post(`/admin/pv-packages/${p.id}/reactivate`);
                      toast.success("Paket reaktiviert");
                      load();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Reaktivieren fehlgeschlagen");
                    }
                  }}
                  data-testid={`pv-pkg-reactivate-${p.id}`}
                  className="h-8 px-3 rounded-lg bg-[#09090B] border border-[#00E575]/50 text-[#00E575] hover:bg-[#00E575]/10 inline-flex items-center gap-1.5 text-[11.5px] font-semibold">
                  <RotateCcw size={12} /> Reaktivieren
                </button>
              )}
                </>
              )}
            </div>
          </section>
          );
        })}
      </main>

      {/* Editor Modal */}
      {editor && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
          onClick={() => !saving && setEditor(null)} data-testid="pv-pkg-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-[#18181B] border border-zinc-700 rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between sticky top-0 bg-[#18181B] border-b border-zinc-800 px-5 py-4 z-10">
              <div>
                <h2 className="font-display text-lg font-bold">{editor.isNew ? "Neues PV-Paket" : "Paket bearbeiten"}</h2>
                <p className="text-[12px] text-zinc-400 mt-0.5">Komponenten wählen · Preise pflegen · Live-Summe wird automatisch berechnet</p>
              </div>
              <button onClick={() => setEditor(null)} disabled={saving}
                className="h-8 w-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 inline-flex items-center justify-center"><X size={14} /></button>
            </div>

            <div className="p-5 space-y-5">
              {/* Stammdaten */}
              <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className={labelCls}>Paket-Name *</label>
                  <input type="text" value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                    data-testid="pv-pkg-edit-name" placeholder="z.B. PV-Starter 6 kWp Süd" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Richt-kWp</label>
                  <input type="number" step="0.1" value={editor.target_kwp}
                    onChange={(e) => setEditor({ ...editor, target_kwp: e.target.value })}
                    data-testid="pv-pkg-edit-kwp" className={inputCls} />
                </div>
                <div className="sm:col-span-3">
                  <label className={labelCls}>Beschreibung</label>
                  <input type="text" value={editor.description}
                    onChange={(e) => setEditor({ ...editor, description: e.target.value })}
                    placeholder="z.B. Kompaktes Einstiegspaket für EFH mit Süddach" className={inputCls} />
                </div>
              </section>

              {/* Live-Preis-Anzeige */}
              <section className="bg-[#00E575]/5 border border-[#00E575]/20 rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-bold">Live-Preis (aus Positionen)</div>
                  <div className="font-mono text-xl font-bold text-[#00E575]" data-testid="pv-pkg-editor-price">
                    {eur(linesSumNet)} <span className="text-[12px] text-zinc-300 font-medium">netto</span>
                  </div>
                  <div className="text-[12px] text-zinc-400 mt-0.5" data-testid="pv-pkg-editor-sum-check">
                    Σ Einzelpreise · 0 % USt (§ 12 Abs. 3 UStG) — Netto = Brutto
                  </div>
                </div>
                {kwpFromModules > 0 && (
                  <div className="text-right">
                    <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-bold">Berechnete Modul-Leistung</div>
                    <div className="font-mono text-lg font-bold text-zinc-100">{kwpFromModules.toFixed(2)} kWp</div>
                  </div>
                )}
              </section>

              {/* Sync-Button */}
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={pullPricesFromComponents}
                  data-testid="pv-pkg-editor-sync-prices"
                  title="Für alle Katalog-Bestandteile den aktuellen Netto-Preis aus PV-Komponenten übernehmen."
                  className="h-8 px-3 rounded bg-[#00E575]/10 border border-[#00E575]/40 text-[#00E575] hover:bg-[#00E575]/20 inline-flex items-center gap-1.5 text-[11.5px] font-bold">
                  <Zap size={11} /> Preise aus Komponenten
                </button>
                <button type="button" onClick={addFreeLine}
                  data-testid="pv-pkg-editor-add-free"
                  className="h-8 px-3 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 inline-flex items-center gap-1.5 text-[11.5px] font-bold">
                  <Plus size={11} /> Freie Position
                </button>
              </div>

              {/* Component Picker */}
              <section className="bg-[#09090B] border border-zinc-800 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-[12px] uppercase tracking-widest text-[#00E575] font-bold">Komponente hinzufügen</h3>
                  <div className="flex gap-1 flex-wrap">
                    {Object.entries(TYPE_META).filter(([k]) => k !== "frei").map(([k, m]) => {
                      const Icon = m.icon;
                      const isActive = pickerType === k;
                      return (
                        <button key={k} onClick={() => setPickerType(k)}
                          data-testid={`pv-pkg-picker-${k}`}
                          className={`px-2.5 py-1 rounded text-[10.5px] font-bold inline-flex items-center gap-1 ${
                            isActive ? "bg-[#00E575]/15 text-[#00E575]" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                          }`}>
                          <Icon size={11} /> {m.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {filteredCatalog.length === 0 ? (
                    <p className="text-[12px] text-zinc-300 text-center py-3 font-medium">Keine aktiven {TYPE_META[pickerType]?.label}-Komponenten — bitte in PV-Komponenten anlegen.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {filteredCatalog.map((c) => (
                        <button key={c.id} type="button" onClick={() => addItem(c.id)}
                          data-testid={`pv-pkg-add-${c.id}`}
                          className="text-left bg-[#18181B] border border-zinc-800 rounded-lg px-3 py-2 hover:border-[#00E575]/40 text-[12px] inline-flex items-center justify-between gap-2 font-medium">
                          <span className="truncate"><b className="text-zinc-100">{c.brand}</b> <span className="text-zinc-400">{c.model}</span></span>
                          <span className="text-[#00E575] font-mono shrink-0">{eur(c.price_eur)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </section>

              {/* Selected items */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-[12px] uppercase tracking-widest text-[#00E575] font-bold">Paket-Inhalt ({editor.items.length})</h3>
                </div>
                {editor.items.length === 0 ? (
                  <p className="text-[12px] text-zinc-300 text-center py-3 border border-dashed border-zinc-800 rounded-lg font-medium">Noch keine Positionen — oben auswählen oder &bdquo;Freie Position&ldquo; hinzufügen.</p>
                ) : (
                  <div className="space-y-2">
                    {editor.items.map((it) => {
                      const c = it.component_id ? compMap[it.component_id] : null;
                      const displayName = it.is_free
                        ? (it.title || "Freie Position")
                        : (c ? `${c.brand} ${c.model}` : <span className="text-red-400">⚠ gelöscht</span>);
                      const unit = _itemUnit(it, compMap);
                      const lineTotal = unit * (Number(it.qty) || 0);
                      return (
                        <div key={it._key} className="bg-[#09090B] border border-zinc-800 rounded-lg p-2.5 flex items-start gap-2 flex-wrap sm:flex-nowrap">
                          <div className="flex-1 min-w-0">
                            {it.is_free ? (
                              <input value={it.title || ""}
                                onChange={(e) => updateFreeTitle(it._key, e.target.value)}
                                data-testid={`pv-pkg-editor-free-title-${it._key}`}
                                placeholder="Bezeichnung (z. B. Sonderleistung Gerüst)"
                                className="h-8 w-full bg-transparent border-0 text-[12px] font-semibold text-zinc-100 outline-none focus:bg-[#09090B] focus:border focus:border-[#00E575] rounded px-1" />
                            ) : (
                              <div className="text-[12px] font-semibold truncate">{displayName}</div>
                            )}
                            <input value={it.note || ""} onChange={(e) => updateNote(it._key, e.target.value)}
                              placeholder={it.is_free ? "Beschreibung (optional)" : "Anmerkung (optional)"}
                              className="mt-0.5 h-6 w-full bg-transparent border-0 text-[10.5px] text-zinc-500 focus:text-zinc-300 outline-none px-0" />
                          </div>
                          <div className="flex flex-col items-end shrink-0">
                            <span className="text-[9px] uppercase tracking-widest text-zinc-300">Preis netto</span>
                            <input type="number" min="0" step="0.01" value={it.ref_price_net_eur || 0}
                              onChange={(e) => updateRefPrice(it._key, e.target.value)}
                              data-testid={`pv-pkg-editor-ref-${it._key}`}
                              placeholder="0,00"
                              className="h-8 w-24 bg-[#09090B] border border-zinc-700 rounded px-2 text-right text-[11.5px] text-amber-200" />
                          </div>
                          <div className="flex flex-col items-end shrink-0">
                            <span className="text-[9px] uppercase tracking-widest text-zinc-300">Menge</span>
                            <input type="number" min="0" step="0.5" value={it.qty}
                              onChange={(e) => updateQty(it._key, e.target.value)}
                              className="h-8 w-16 bg-[#09090B] border border-zinc-700 rounded px-2 text-right text-[11.5px]" />
                          </div>
                          <div className="flex flex-col items-end shrink-0 min-w-[80px]">
                            <span className="text-[9px] uppercase tracking-widest text-zinc-300">Zeilen-∑</span>
                            <span className="h-8 inline-flex items-center font-mono text-[12px] text-zinc-100">{eur(lineTotal)}</span>
                          </div>
                          <button onClick={() => removeItem(it._key)}
                            data-testid={`pv-pkg-editor-remove-${it._key}`}
                            className="h-7 w-7 rounded bg-red-500/10 border border-red-500/40 text-red-400 hover:bg-red-500/20 inline-flex items-center justify-center shrink-0 self-end">
                            <X size={12} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              <section>
                <label className={labelCls}>Notizen (intern)</label>
                <textarea rows="2" value={editor.notes}
                  onChange={(e) => setEditor({ ...editor, notes: e.target.value })}
                  className={`${inputCls} h-auto py-2`} placeholder="z.B. Eignung, Logistik-Hinweise…" />
              </section>

              <section>
                <label className="inline-flex items-center gap-2 text-[13px] text-zinc-200">
                  <input type="checkbox" checked={editor.active}
                    onChange={(e) => setEditor({ ...editor, active: e.target.checked })}
                    data-testid="pv-pkg-edit-active" className="accent-[#00E575]" />
                  Paket ist aktiv (im Konfigurator wählbar)
                </label>
              </section>

              {!editor.isNew && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={14} />
                  <p className="text-[11.5px] text-amber-200 leading-relaxed">
                    Änderungen wirken nur für NEUE Konfigurationen. Bereits erstellte PV-Konfigurationen behalten ihre damaligen Komponenten + Preise (Audit-Sicherheit).
                  </p>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-[#18181B] border-t border-zinc-800 px-5 py-3 flex justify-end gap-2">
              <button onClick={() => setEditor(null)} disabled={saving}
                className="h-9 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[12px] text-zinc-200 font-medium">Abbrechen</button>
              <button onClick={save} disabled={saving} data-testid="pv-pkg-save"
                className="h-9 px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964] disabled:opacity-50">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                {editor.isNew ? "Anlegen" : "Speichern"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Import-Preview Modal (Schritt 2: Zeilen prüfen → committen) */}
      {importPreview && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
          onClick={() => !committing && setImportPreview(null)} data-testid="pv-pkg-import-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-[#18181B] border border-zinc-700 rounded-2xl max-w-5xl w-full max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between sticky top-0 bg-[#18181B] border-b border-zinc-800 px-5 py-4 z-10">
              <div className="flex items-center gap-2">
                <Sparkles className="text-[#00E575]" size={18} />
                <div>
                  <h2 className="font-display text-lg font-bold">Import-Vorschau</h2>
                  <p className="text-[12px] text-zinc-400 mt-0.5">Von der KI erkannte Positionen · vor dem Anlegen prüfen und ggf. Komponenten-Zuordnung anpassen</p>
                </div>
              </div>
              <button onClick={() => setImportPreview(null)} disabled={committing}
                className="h-8 w-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 inline-flex items-center justify-center"><X size={14} /></button>
            </div>

            <div className="p-5 space-y-4">
              {/* Kopfdaten */}
              <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className={labelCls}>Paket-Name *</label>
                  <input type="text" value={importPreview.name}
                    onChange={(e) => setImportPreview({ ...importPreview, name: e.target.value })}
                    data-testid="pv-pkg-import-name" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Erkannte kWp</label>
                  <input type="number" step="0.1" value={importPreview.target_kwp}
                    onChange={(e) => setImportPreview({ ...importPreview, target_kwp: e.target.value })}
                    data-testid="pv-pkg-import-kwp" className={inputCls} />
                </div>
                <div className="sm:col-span-3">
                  <label className={labelCls}>Beschreibung</label>
                  <input type="text" value={importPreview.description}
                    onChange={(e) => setImportPreview({ ...importPreview, description: e.target.value })}
                    className={inputCls} />
                </div>
              </section>

              {/* Live-Summe */}
              <div className="bg-[#00E575]/5 border border-[#00E575]/20 rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-bold">Import-Summe (aus Positionen)</div>
                  <div className="text-[10.5px] text-zinc-500 mt-0.5">{importPreview.items.length} Positionen</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-xl font-bold text-[#00E575]" data-testid="pv-pkg-import-gross">{eur(importGrossPreview)} <span className="text-[10.5px] text-zinc-500 font-medium">netto = brutto (0 % USt)</span></div>
                </div>
              </div>

              {/* Zeilen-Tabelle */}
              <section>
                <div className="overflow-x-auto -mx-1">
                  <table className="w-full text-[12px] min-w-[820px] font-medium">
                    <thead>
                      <tr className="text-[10.5px] uppercase tracking-widest text-zinc-300 font-bold border-b border-zinc-800">
                        <th className="text-left py-1.5 px-2">Rolle</th>
                        <th className="text-left py-1.5 px-2">Bezeichnung / Bez.</th>
                        <th className="text-left py-1.5 px-2">Komponenten-Zuordnung</th>
                        <th className="text-right py-1.5 px-2">Menge</th>
                        <th className="text-right py-1.5 px-2">Netto EP</th>
                        <th className="text-right py-1.5 px-2">Zeile</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.items.map((it) => {
                        const Meta = TYPE_META[it.role] || TYPE_META.addon;
                        const Icon = Meta.icon;
                        // matched component options: alle aktiven mit passendem type (falls role != frei)
                        const typeFilter = it.role === "frei" ? null : it.role;
                        const opts = catalog.filter(c => c.active !== false && (!typeFilter || c.type === typeFilter));
                        const lineTotal = (Number(it.price_net_eur) || 0) * (Number(it.qty) || 0);
                        return (
                          <tr key={it._key} className="border-b border-zinc-800/40 align-top">
                            <td className="py-1.5 px-2">
                              <select value={it.role}
                                onChange={(e) => updateImportItem(it._key, { role: e.target.value, matched_component_id: null })}
                                data-testid={`pv-pkg-import-role-${it._key}`}
                                className="h-8 bg-[#09090B] border border-zinc-700 rounded px-1.5 text-[12px] text-zinc-100 outline-none focus:border-[#00E575]">
                                {Object.entries(TYPE_META).map(([k, m]) => (
                                  <option key={k} value={k}>{m.label}</option>
                                ))}
                              </select>
                              <div className="text-[12px] text-zinc-300 mt-0.5 inline-flex items-center gap-1"><Icon size={9} /> {Meta.label}</div>
                            </td>
                            <td className="py-1.5 px-2 min-w-[180px]">
                              <input value={`${it.brand || ""} ${it.model || ""}`.trim()}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const parts = val.split(" ");
                                  updateImportItem(it._key, { brand: parts[0] || "", model: parts.slice(1).join(" ") });
                                }}
                                className="h-8 w-full bg-[#09090B] border border-zinc-700 rounded px-2 text-[11.5px] text-zinc-100 outline-none focus:border-[#00E575]"
                                placeholder="Marke Modell" />
                              {it.description && (
                                <div className="text-[12px] text-zinc-300 mt-0.5 truncate">{it.description}</div>
                              )}
                            </td>
                            <td className="py-1.5 px-2 min-w-[220px]">
                              {it.role === "frei" ? (
                                <span className="text-[10.5px] text-zinc-500 italic">Freie Position (kein Komponenten-Bezug)</span>
                              ) : (
                                <>
                                  <select value={it.matched_component_id || ""}
                                    onChange={(e) => updateImportItem(it._key, { matched_component_id: e.target.value || null })}
                                    data-testid={`pv-pkg-import-match-${it._key}`}
                                    className="h-8 w-full bg-[#09090B] border border-zinc-700 rounded px-1.5 text-[12px] text-zinc-100 outline-none focus:border-[#00E575]">
                                    <option value="">— keine (als freie Zeile anlegen) —</option>
                                    {opts.map(c => (
                                      <option key={c.id} value={c.id}>{c.brand} {c.model} · {eur(c.price_eur)}</option>
                                    ))}
                                  </select>
                                  {it.matched_component_id && it.match_confidence > 0 && (
                                    <div className={`text-[9.5px] mt-0.5 font-bold uppercase tracking-widest ${
                                      it.match_confidence >= 0.8 ? "text-[#00E575]"
                                      : it.match_confidence >= 0.5 ? "text-amber-400" : "text-red-400"
                                    }`}>Match {Math.round(it.match_confidence * 100)}%</div>
                                  )}
                                </>
                              )}
                            </td>
                            <td className="py-1.5 px-2 text-right">
                              <input type="number" step="0.5" value={it.qty}
                                onChange={(e) => updateImportItem(it._key, { qty: e.target.value })}
                                data-testid={`pv-pkg-import-qty-${it._key}`}
                                className="h-8 w-16 bg-[#09090B] border border-zinc-700 rounded px-2 text-right text-[11.5px] text-zinc-100 outline-none focus:border-[#00E575]" />
                            </td>
                            <td className="py-1.5 px-2 text-right">
                              <input type="number" step="0.01" value={it.price_net_eur}
                                onChange={(e) => updateImportItem(it._key, { price_net_eur: e.target.value })}
                                data-testid={`pv-pkg-import-price-${it._key}`}
                                className="h-8 w-24 bg-[#09090B] border border-zinc-700 rounded px-2 text-right text-[11.5px] text-amber-200 outline-none focus:border-[#00E575]" />
                            </td>
                            <td className="py-1.5 px-2 text-right font-mono text-[11.5px] text-zinc-100 whitespace-nowrap">{eur(lineTotal)}</td>
                            <td className="py-1.5 px-2 text-right">
                              <button onClick={() => removeImportItem(it._key)}
                                data-testid={`pv-pkg-import-remove-${it._key}`}
                                className="h-7 w-7 rounded bg-red-500/10 border border-red-500/40 text-red-400 hover:bg-red-500/20 inline-flex items-center justify-center">
                                <X size={11} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>

              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={13} />
                <p className="text-[12px] text-amber-200 leading-relaxed">
                  <b>Gut zu wissen:</b> Zeilen mit Match-Score unter 80 % bitte prüfen — bei &bdquo;keine (als freie Zeile anlegen)&ldquo; wird die Position als freie Zeile im Paket gespeichert und ist NICHT mit einer PV-Komponente verknüpft. Preise/Mengen kannst du hier direkt korrigieren.
                </p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-[#18181B] border-t border-zinc-800 px-5 py-3 flex justify-end gap-2">
              <button onClick={() => setImportPreview(null)} disabled={committing}
                className="h-9 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[12px] text-zinc-200 font-medium">Verwerfen</button>
              <button onClick={commitImport} disabled={committing} data-testid="pv-pkg-import-commit"
                className="h-9 px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964] disabled:opacity-50">
                {committing ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                Paket anlegen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
